import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import CyclingTitle from './primitives/CyclingTitle';
import { computeGoalProgress, computeGoalStatus, formatTargetDate } from '../utils/goals';
import { milesInRange } from '../utils/trainStats';
import {
  Target, Dumbbell, DollarSign, Lightbulb, Briefcase, Trophy,
  Heart, BookOpen, Plane, Home, Music, Code, Camera, Leaf,
  Zap, Star, Flag, Coffee, Bike, Waves, Brain, Palette,
  CircleDot, Activity, Sparkles, Check,
} from 'lucide-react';


// ── Goal icon system ──────────────────────────────────────────────────────────
const GOAL_ICON_MAP = {
  Target, Dumbbell, DollarSign, Lightbulb, Briefcase, Trophy,
  Heart, BookOpen, Plane, Home, Music, Code, Camera, Leaf,
  Zap, Star, Flag, Coffee, Bike, Waves, Brain, Palette,
};

const GOAL_ICON_CATEGORIES = [
  { label: 'goals',    icons: ['Target','Trophy','Flag','Star','Zap'] },
  { label: 'fitness',  icons: ['Dumbbell','Bike','Waves','Heart','Leaf'] },
  { label: 'mind',     icons: ['Brain','BookOpen','Lightbulb','Palette','Code'] },
  { label: 'life',     icons: ['Home','Plane','Music','Camera','Coffee'] },
  { label: 'work',     icons: ['Briefcase','DollarSign'] },
];

function renderGoalIcon(iconName, size = 16) {
  if (iconName && GOAL_ICON_MAP[iconName]) {
    const Icon = GOAL_ICON_MAP[iconName];
    return <Icon size={size} strokeWidth={1.5} />;
  }
  return <CircleDot size={size} strokeWidth={1.5} />;
}

function GoalIconPicker({ selected, onSelect }) {
  return (
    <div className="gip-wrap">
      <div className="gip-cat-lbl">none</div>
      <div className="gip-grid">
        <button type="button" className={`gip-tile ${!selected ? 'gip-tile-sel' : ''}`}
          onClick={() => onSelect('')}>
          <CircleDot size={16} strokeWidth={1.5} />
        </button>
      </div>
      {GOAL_ICON_CATEGORIES.map(cat => (
        <div key={cat.label}>
          <div className="gip-cat-lbl">{cat.label}</div>
          <div className="gip-grid">
            {cat.icons.map(name => {
              const Icon = GOAL_ICON_MAP[name];
              if (!Icon) return null;
              return (
                <button key={name} type="button"
                  className={`gip-tile ${selected === name ? 'gip-tile-sel' : ''}`}
                  onClick={() => onSelect(name)}>
                  <Icon size={16} strokeWidth={1.5} />
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const TODAY = new Date().toISOString().slice(0, 10);

// ── Helpers ───────────────────────────────────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.round((new Date(dateStr + 'T12:00:00') - new Date()) / 86400000);
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDays(n) {
  if (n === null) return '';
  if (n < 0) { const a = Math.abs(n); return `${a} ${a===1?'day':'days'} ago`; }
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n < 7) return `${n} ${n===1?'day':'days'}`;
  if (n < 30) { const w = Math.round(n/7); return `${w} ${w===1?'week':'weeks'}`; }
  const mo = Math.round(n/30); return `${mo} ${mo===1?'month':'months'}`;
}

function fmtMoney(n) {
  if (!n && n !== 0) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

// ── Type system ───────────────────────────────────────────────────────────────
const TYPE_COLOR = {
  race:     'var(--accent)',
  fitness:  'var(--accent-s)',
  finance:  'var(--accent-g)',
  personal: 'var(--accent-d)',
  project:  'var(--t2)',
};

const TYPE_LABEL = {
  race: 'race', fitness: 'fitness', finance: 'finance', personal: 'personal', project: 'project',
};

const STATUS_CONFIG = {
  'ahead':          { label: 'ahead',    color: 'var(--accent-g)' },
  'on-track':       { label: 'on track', color: 'var(--accent-g)' },
  'behind':         { label: 'behind',   color: 'var(--t3)' },
  'at-risk':        { label: 'at risk',  color: 'var(--accent)' },
  'complete':       { label: 'complete', color: 'var(--accent-g)' },
  'no-target-date': { label: null,       color: null },
};

const VIEWS = ['active', 'completed'];
const EMPTY_FORM = { title: '', type: 'personal', targetDate: '', note: '', progress: 0, targetAmount: '', currentAmount: '', milestones: [], icon: '' };

// ── GoalCarousel ──────────────────────────────────────────────────────────────
function GoalCarousel({ goals, completedCount }) {
  const [slide, setSlide] = useState(0);
  const timerRef = useRef(null);

  const slides = useMemo(() => {
    const s = [];
    const active = goals.filter(g => !g.archived && !g.completed);
    if (active.length === 0) return s;

    const avgProg = Math.round(active.reduce((a, g) => a + (g.progress || computeGoalProgress(g)), 0) / active.length);
    if (avgProg > 0) s.push({ value: `${avgProg}%`, context: 'average progress', accent: `across ${active.length} goal${active.length !== 1 ? 's' : ''}` });

    const atRisk = active.filter(g => { const st = computeGoalStatus(g); return st.status === 'at-risk'; });
    if (atRisk.length > 0) s.push({ value: atRisk.length, context: `goal${atRisk.length !== 1 ? 's' : ''} at risk`, accent: 'needs attention' });

    const soonest = active.filter(g => g.targetDate).sort((a, b) => a.targetDate.localeCompare(b.targetDate))[0];
    if (soonest) {
      const d = daysUntil(soonest.targetDate);
      if (d !== null && d >= 0) s.push({ value: fmtDays(d), context: `until ${soonest.title.toLowerCase()}`, accent: fmtDate(soonest.targetDate) });
    }

    if (completedCount > 0) s.push({ value: completedCount, context: `goal${completedCount !== 1 ? 's' : ''} completed`, accent: 'all time' });

    const ahead = active.filter(g => computeGoalStatus(g).status === 'ahead');
    if (ahead.length > 0) s.push({ value: ahead.length, context: `goal${ahead.length !== 1 ? 's' : ''} ahead of pace`, accent: 'keep going' });

    return s;
  }, [goals, completedCount]);

  const start = useCallback(() => {
    clearInterval(timerRef.current);
    if (slides.length > 1) timerRef.current = setInterval(() => setSlide(i => (i + 1) % slides.length), 3500);
  }, [slides.length]);

  useEffect(() => { setSlide(0); start(); return () => clearInterval(timerRef.current); }, [slides.length]);

  function advance() { setSlide(i => (i + 1) % slides.length); start(); }

  if (slides.length === 0) return null;
  const sl = slides[slide] || slides[0];

  return (
    <div className="gp-carousel" onClick={advance}>
      <div className="gp-carousel-slide" key={slide}>
        <div className="gp-carousel-val">{sl.value}</div>
        <div className="gp-carousel-ctx">{sl.context}</div>
        {sl.accent && <div className="gp-carousel-accent">{sl.accent}</div>}
      </div>
      {slides.length > 1 && (
        <div className="gp-carousel-dots">
          {slides.map((_, i) => (
            <div key={i} className={`gp-carousel-dot ${i === slide ? 'gp-carousel-dot-active' : ''}`}
              onClick={e => { e.stopPropagation(); setSlide(i); start(); }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── RaceHeroCard ──────────────────────────────────────────────────────────────
function RaceHeroCard({ goal, workouts = [], setTab }) {
  const days = daysUntil(goal.targetDate);
  const now = new Date();
  const cycleStart = (() => {
    const d = new Date(now); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10);
  })();
  const trainingMiles = milesInRange(workouts || [], cycleStart, TODAY);

  return (
    <div className="gp-race-hero">
      <div className="gp-race-hero-left">
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}><div style={{color:"var(--accent)"}}>{renderGoalIcon(goal.icon, 14)}</div><div className="gp-race-hero-lbl" style={{marginBottom:0}}>race</div></div>
        <div className="gp-race-hero-title">{goal.title}</div>
        {goal.targetDate && <div className="gp-race-hero-date">{fmtDate(goal.targetDate)}</div>}
      </div>
      <div className="gp-race-hero-right">
        {days !== null && days >= 0 && (
          <>
            <div className="gp-race-hero-count">{days}</div>
            <div className="gp-race-hero-count-lbl">days</div>
          </>
        )}
        {days !== null && days < 0 && <div className="gp-race-hero-count-lbl" style={{ color: 'var(--t3)' }}>past</div>}
      </div>
      {trainingMiles > 0 && (
        <div className="gp-race-hero-miles">{trainingMiles.toFixed(0)} mi trained · last 90d</div>
      )}
    </div>
  );
}

// ── GoalCard ──────────────────────────────────────────────────────────────────
function GoalCard({ g, expanded, onToggle, onEdit, onComplete, onArchive, onDelete, onProgressChange, isCompleted }) {
  const progress = g.progress !== undefined ? g.progress : computeGoalProgress(g);
  const statusInfo = computeGoalStatus(g);
  const status = STATUS_CONFIG[statusInfo.status];
  const days = daysUntil(g.targetDate);
  const typeColor = TYPE_COLOR[g.type] || 'var(--t3)';

  const hasAmount = g.targetAmount > 0;
  const currentAmt = g.currentAmount || 0;

  return (
    <div className="gp-card" style={{ borderLeft: `2px solid ${typeColor}` }}>
      {/* Main row */}
      <div className="gp-card-main" onClick={onToggle}>
        <div className="gp-card-icon" style={{color: typeColor, background: `color-mix(in srgb, ${typeColor} 10%, transparent)`}}>{renderGoalIcon(g.icon, 14)}</div>
        <div className="gp-card-body">
          <div className="gp-card-title">{g.title}</div>
          <div className="gp-card-meta">
            <span className="gp-card-type">{TYPE_LABEL[g.type] || g.type}</span>
            {!isCompleted && days !== null && (
              <span className="gp-card-date" style={{ color: days < 0 ? 'var(--t4)' : days <= 7 ? 'var(--accent)' : 'var(--t3)' }}>
                · {fmtDays(days)}
              </span>
            )}
            {isCompleted && g.completedAt && (
              <span className="gp-card-date">· completed {fmtDate(g.completedAt)}</span>
            )}
            {!isCompleted && status.label && (
              <span className="gp-card-status" style={{ color: status.color }}>· {status.label}</span>
            )}
            {hasAmount && !isCompleted && (
              <span className="gp-card-date">· {fmtMoney(currentAmt)} / {fmtMoney(g.targetAmount)}</span>
            )}
          </div>
          {/* Progress bar — tap to nudge +5% when collapsed */}
          <div
            className="gp-card-bar"
            onClick={!isCompleted && !expanded && !hasAmount ? (e) => {
              e.stopPropagation();
              const next = Math.min(100, (progress||0) + 5);
              onProgressChange && onProgressChange(g.id, next);
            } : undefined}
            title={!isCompleted && !expanded && !hasAmount ? `tap to add 5% progress (${Math.round(progress||0)}%)` : undefined}
            style={!isCompleted && !expanded && !hasAmount ? {cursor:'pointer'} : {}}
          >
            <div className="gp-card-bar-fill"
              style={{ width: `${Math.min(progress, 100)}%`, background: progress >= 100 ? 'var(--accent-g)' : isCompleted ? 'var(--accent-g)' : 'var(--accent)' }} />
          </div>
        </div>
        <div className="gp-card-pct" style={{ color: progress > 0 ? 'var(--ink)' : 'var(--t4)' }}>
          {Math.round(progress)}%
        </div>
      </div>

      {/* Expanded drawer */}
      {expanded && (
        <div className="gp-card-expand">
          {g.note && <div className="gp-card-note">{g.note}</div>}

          {/* Milestones */}
          {g.milestones && g.milestones.length > 0 && (
            <div className="gp-milestones">
              {g.milestones.map((m, i) => (
                <div key={i} className="gp-milestone-row">
                  <div className={`gp-milestone-check ${m.done ? 'gp-milestone-check-done' : ''}`}
                    onClick={() => onProgressChange && onProgressChange(g.id, undefined, i)}>
                    {m.done && <svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3l2 2 4-4" stroke="var(--accent-g)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                  <span className="gp-milestone-label" style={{ textDecoration: m.done ? 'line-through' : 'none', color: m.done ? 'var(--t3)' : 'var(--ink)' }}>{m.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Progress slider for manual goals */}
          {!hasAmount && !isCompleted && (
            <div className="gp-slider-wrap">
              <span className="gp-slider-lbl">progress</span>
              <input type="range" min={0} max={100} step={5} value={progress}
                className="gp-slider"
                onChange={e => onProgressChange && onProgressChange(g.id, parseInt(e.target.value))} />
              <span className="gp-slider-val">{Math.round(progress)}%</span>
            </div>
          )}

          {/* Amount inputs for finance goals */}
          {hasAmount && !isCompleted && (
            <div className="gp-amount-row">
              <div className="gp-amount-field">
                <div className="gp-amount-lbl">current</div>
                <input className="gp-amount-input" type="number" placeholder="0"
                  value={g.currentAmount || ''} onChange={e => onProgressChange && onProgressChange(g.id, undefined, undefined, parseFloat(e.target.value) || 0)} />
              </div>
              <div className="gp-amount-sep">→</div>
              <div className="gp-amount-field">
                <div className="gp-amount-lbl">target</div>
                <div className="gp-amount-input" style={{ display: 'flex', alignItems: 'center', color: 'var(--t3)', fontSize: 13 }}>{fmtMoney(g.targetAmount)}</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="gp-card-actions">
            <button className="gp-act-btn" onClick={onEdit}>edit</button>
            {!isCompleted && <button className="gp-act-btn gp-act-done" onClick={onComplete}>mark complete</button>}
            <button className="gp-act-btn" onClick={onArchive}>{isCompleted ? 'restore' : 'archive'}</button>
            <button className="gp-act-btn gp-act-del" onClick={onDelete}>delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── GoalForm ──────────────────────────────────────────────────────────────────
function GoalForm({ form, setForm, onSave, onCancel, editId }) {
  const [newMilestone, setNewMilestone] = useState('');

  function addMilestone() {
    const t = newMilestone.trim();
    if (!t) return;
    setForm(p => ({ ...p, milestones: [...(p.milestones || []), { text: t, done: false }] }));
    setNewMilestone('');
  }

  function removeMilestone(i) {
    setForm(p => ({ ...p, milestones: p.milestones.filter((_, idx) => idx !== i) }));
  }

  return (
    <div className="gp-form">
      <div className="gp-form-hdr">{editId ? 'edit goal' : 'new goal'}</div>

      <div className="gp-field">
        <label className="gp-lbl">goal</label>
        <input className="gp-input" placeholder="what do you want to achieve?" autoFocus
          value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }} />
      </div>

      <div className="gp-field">
        <label className="gp-lbl">type</label>
        <div className="gp-pill-row">
          {['race', 'fitness', 'finance', 'personal', 'project'].map(t => (
            <button key={t} className={`gp-pill ${form.type === t ? 'gp-pill-active' : ''}`}
              onClick={() => setForm(p => ({ ...p, type: t }))}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="gp-field">
        <label className="gp-lbl">target date</label>
        <input type="date" className="gp-input" style={{ maxWidth: 180 }}
          value={form.targetDate} onChange={e => setForm(p => ({ ...p, targetDate: e.target.value }))} />
      </div>

      {/* Finance: amount fields */}
      {form.type === 'finance' && (
        <div className="gp-field">
          <label className="gp-lbl">target amount</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="gp-input" type="number" placeholder="target $" style={{ maxWidth: 120 }}
              value={form.targetAmount} onChange={e => setForm(p => ({ ...p, targetAmount: e.target.value }))} />
            <input className="gp-input" type="number" placeholder="current $" style={{ maxWidth: 120 }}
              value={form.currentAmount} onChange={e => setForm(p => ({ ...p, currentAmount: e.target.value }))} />
          </div>
        </div>
      )}

      <div className="gp-field">
        <label className="gp-lbl">note</label>
        <textarea className="gp-textarea" placeholder="context, motivation, or what success looks like…"
          value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
      </div>

      <div className="gp-field">
        <label className="gp-lbl">icon</label>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
          <div style={{width:32,height:32,borderRadius:'var(--r2)',background:'var(--s2)',border:'0.5px solid var(--b2)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t2)'}}>
            {renderGoalIcon(form.icon, 14)}
          </div>
          <button type="button" className="gp-act-btn"
            onClick={()=>setForm(p=>({...p,_showIconPicker:!p._showIconPicker}))}>
            {form._showIconPicker ? 'done' : 'choose icon'}
          </button>
        </div>
        {form._showIconPicker && <GoalIconPicker selected={form.icon} onSelect={v=>setForm(p=>({...p,icon:v,_showIconPicker:false}))}/>}
      </div>

      {/* Milestones */}
      <div className="gp-field">
        <label className="gp-lbl">milestones</label>
        {(form.milestones || []).map((m, i) => (
          <div key={i} className="gp-ms-row">
            <span className="gp-ms-text">{m.text}</span>
            <button className="gp-ms-rm" onClick={() => removeMilestone(i)}>×</button>
          </div>
        ))}
        <div className="gp-ms-add">
          <input className="gp-ms-input" placeholder="add a milestone…"
            value={newMilestone} onChange={e => setNewMilestone(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addMilestone(); }} />
          <button className="gp-act-btn" onClick={addMilestone}>+</button>
        </div>
      </div>

      <div className="gp-form-actions">
        <button className="gp-cancel-btn" onClick={onCancel}>cancel</button>
        <button className="gp-save-btn" onClick={onSave}>save</button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GoalsPage({ goals = [], workouts = [], st = {}, setSt, notify, setTab }) {
  const [view, setView]         = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [expandedId, setExpandedId] = useState(null);

  const active    = useMemo(() => goals.filter(g => !g.archived && !g.completed), [goals]);
  const completed = useMemo(() => goals.filter(g => g.completed || g.archived), [goals]);
  // Prefer dedicated st.raceName/st.raceDate over goal-derived race
  const raceGoal  = useMemo(() => {
    if (st?.raceName && st?.raceDate) {
      return { id: '_dedicated_race', title: st.raceName, targetDate: st.raceDate, type: 'race', progress: 0, icon: '', milestones: [] };
    }
    return active.find(g => g.type === 'race') || null;
  }, [active, st?.raceName, st?.raceDate]);
  const nonRace   = useMemo(() => active.filter(g => g.type !== 'race'), [active]);

  const sortedActive = useMemo(() => [...nonRace].sort((a, b) => {
    const da = daysUntil(a.targetDate), db = daysUntil(b.targetDate);
    if (da === null && db === null) return 0;
    if (da === null) return 1;
    if (db === null) return -1;
    return da - db;
  }), [nonRace]);

  const sortedCompleted = useMemo(() => [...completed].sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || '')), [completed]);

  function openAdd() { setForm(EMPTY_FORM); setEditId(null); setShowForm(true); }

  function openEdit(g) {
    setForm({ title: g.title || '', type: g.type || 'personal', targetDate: g.targetDate || '', note: g.note || '', progress: g.progress || 0, targetAmount: g.targetAmount || '', currentAmount: g.currentAmount || '', milestones: g.milestones || [], icon: g.icon || '' });
    setEditId(g.id);
    setShowForm(true);
  }

  function saveGoal() {
    if (!form.title.trim()) return;
    const tAmt = parseFloat(form.targetAmount) || 0;
    const cAmt = parseFloat(form.currentAmount) || 0;
    const computedProg = tAmt > 0 ? Math.round((cAmt / tAmt) * 100) : (form.progress || 0);
    setSt(p => {
      const ex = p.goals || [];
      const data = { ...form, title: form.title.trim(), targetAmount: tAmt || undefined, currentAmount: cAmt || undefined, progress: computedProg, icon: form.icon || undefined };
      if (editId) return { ...p, goals: ex.map(g => g.id === editId ? { ...g, ...data } : g) };
      return { ...p, goals: [...ex, { id: `g_${Date.now()}`, ...data, createdAt: TODAY }] };
    });
    setShowForm(false); setEditId(null);
    notify(editId ? 'Goal updated' : 'Goal added');
  }

  function updateProgress(id, val, milestoneIdx, newCurrentAmount) {
    setSt(p => ({
      ...p,
      goals: (p.goals || []).map(g => {
        if (g.id !== id) return g;
        if (milestoneIdx !== undefined) {
          const ms = [...(g.milestones || [])];
          ms[milestoneIdx] = { ...ms[milestoneIdx], done: !ms[milestoneIdx].done };
          const donePct = ms.length > 0 ? Math.round((ms.filter(m => m.done).length / ms.length) * 100) : g.progress;
          return { ...g, milestones: ms, progress: donePct };
        }
        if (newCurrentAmount !== undefined) {
          const prog = g.targetAmount > 0 ? Math.round((newCurrentAmount / g.targetAmount) * 100) : g.progress;
          return { ...g, currentAmount: newCurrentAmount, progress: prog };
        }
        return { ...g, progress: val };
      }),
    }));
  }

  function markComplete(id) {
    setSt(p => ({ ...p, goals: (p.goals || []).map(g => g.id === id ? { ...g, completed: true, completedAt: TODAY, progress: 100 } : g) }));
    notify('Goal completed');
  }

  function archiveGoal(id) {
    const g = goals.find(x => x.id === id);
    if (g?.completed) {
      setSt(p => ({ ...p, goals: (p.goals || []).map(x => x.id === id ? { ...x, completed: false, archived: false } : x) }));
      notify('Goal restored');
    } else {
      setSt(p => ({ ...p, goals: (p.goals || []).map(g => g.id === id ? { ...g, archived: true } : g) }));
    }
  }

  function deleteGoal(id) {
    setSt(p => ({ ...p, goals: (p.goals || []).filter(g => g.id !== id) }));
    notify('Goal removed');
  }

  const CSS = `
.gp{max-width:640px;margin:0 auto;padding:var(--sp4) var(--sp4) var(--sp12);font-family:var(--sans);}
@media(max-width:768px){.gp{padding-bottom:calc(var(--bot-nav-h,56px)+var(--sp10)+var(--safe-bottom,0px));}}
.gp-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--sp5);}
.gp-add-btn{height:28px;padding:0 12px;background:var(--accent);border:none;border-radius:var(--r2);font-family:var(--sans);font-size:var(--fs-xs);font-weight:500;color:var(--on-accent);cursor:pointer;transition:opacity .12s;}
.gp-add-btn:hover{opacity:.85;}
/* carousel */
.gp-carousel{background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);height:110px;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;position:relative;overflow:hidden;margin-bottom:var(--sp4);-webkit-tap-highlight-color:transparent;}
.gp-carousel-slide{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;animation:gp-fade .3s ease;padding:0 16px;text-align:center;}
@keyframes gp-fade{from{opacity:0}to{opacity:1}}
.gp-carousel-val{font-family:var(--sans);font-size:34px;font-weight:300;color:var(--ink);line-height:1;}
.gp-carousel-ctx{font-family:var(--sans);font-size:13px;color:var(--t2);}
.gp-carousel-accent{font-family:var(--sans);font-size:11px;color:var(--accent);}
.gp-carousel-dots{position:absolute;bottom:8px;display:flex;gap:5px;}
.gp-carousel-dot{width:4px;height:4px;border-radius:50%;background:var(--s3);cursor:pointer;}
.gp-carousel-dot-active{background:var(--accent);}
/* race hero */
.gp-race-hero{background:var(--s1);border:0.5px solid var(--s2);border-left:2px solid var(--accent);border-radius:var(--r3);padding:16px;margin-bottom:var(--sp4);cursor:pointer;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;position:relative;overflow:hidden;transition:border-color .15s;}
.gp-race-hero:hover{border-color:var(--b2);}
.gp-race-hero-left{flex:1;min-width:0;}
.gp-race-hero-lbl{font-family:var(--sans);font-size:10px;letter-spacing:0.05em;text-transform:lowercase;color:var(--t3);font-weight:400;margin-bottom:4px;}
.gp-race-hero-title{font-family:var(--sans);font-size:17px;font-weight:400;color:var(--ink);margin-bottom:3px;line-height:1.2;}
.gp-race-hero-date{font-family:var(--sans);font-size:12px;color:var(--t3);}
.gp-race-hero-right{flex-shrink:0;text-align:center;}
.gp-race-hero-count{font-family:var(--sans);font-size:36px;font-weight:300;color:var(--ink);line-height:1;}
.gp-race-hero-count-lbl{font-family:var(--sans);font-size:11px;color:var(--t3);}
.gp-race-hero-miles{position:absolute;bottom:10px;right:16px;font-family:var(--sans);font-size:10px;color:var(--t4);}
/* section label */
.gp-section-lbl{font-family:var(--sans);font-size:11px;font-weight:400;color:var(--t3);letter-spacing:0.05em;text-transform:lowercase;margin-bottom:8px;}
/* goal card */
.gp-card{background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);margin-bottom:8px;overflow:hidden;transition:border-color .15s;}
.gp-card:hover{border-color:var(--b2);}
.gp-card-main{padding:13px 16px 13px 14px;cursor:pointer;display:flex;align-items:center;gap:10px;}
.gp-card-body{flex:1;min-width:0;}
.gp-card-title{font-family:var(--sans);font-size:14px;font-weight:400;color:var(--ink);margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.gp-card-meta{display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-bottom:6px;}
.gp-card-type{font-family:var(--sans);font-size:10px;letter-spacing:.04em;color:var(--t3);}
.gp-card-date{font-family:var(--sans);font-size:11px;color:var(--t3);}
.gp-card-status{font-family:var(--sans);font-size:11px;}
.gp-card-bar{height:3px;background:var(--s3);border-radius:2px;overflow:hidden;}
.gp-card-bar-fill{height:100%;border-radius:2px;transition:width .4s ease;}
.gp-card-pct{font-family:var(--sans);font-size:12px;font-weight:400;flex-shrink:0;min-width:32px;text-align:right;}
/* expand */
.gp-card-expand{padding:0 14px 13px;display:flex;flex-direction:column;gap:10px;border-top:0.5px solid var(--s2);}
.gp-card-note{font-family:var(--sans);font-size:12px;color:var(--t3);line-height:1.55;padding-top:2px;}
/* milestones */
.gp-milestones{display:flex;flex-direction:column;gap:6px;}
.gp-milestone-row{display:flex;align-items:center;gap:8px;padding-left:16px;margin-top:4px;position:relative;}.gp-milestone-row::before{content:"";position:absolute;left:4px;top:50%;width:8px;height:1px;background:var(--b2);}
.gp-milestone-check{width:16px;height:16px;border-radius:4px;border:0.5px solid var(--s3);background:transparent;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .12s;}
.gp-milestone-check-done{border-color:var(--accent-g);background:color-mix(in srgb,var(--accent-g) 12%,transparent);}
.gp-milestone-label{font-family:var(--sans);font-size:12px;color:var(--ink);}
/* slider */
.gp-slider-wrap{display:flex;align-items:center;gap:10px;}
.gp-slider-lbl{font-family:var(--sans);font-size:11px;color:var(--t3);white-space:nowrap;}
.gp-slider{flex:1;accent-color:var(--accent);}
.gp-slider-val{font-family:var(--sans);font-size:11px;color:var(--t3);min-width:28px;text-align:right;}
/* amount */
.gp-amount-row{display:flex;align-items:center;gap:10px;}
.gp-amount-field{flex:1;}
.gp-amount-lbl{font-family:var(--sans);font-size:10px;color:var(--t3);margin-bottom:3px;}
.gp-amount-input{width:100%;height:30px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r);padding:0 8px;font-family:var(--sans);font-size:12px;color:var(--ink);outline:none;box-sizing:border-box;}
.gp-amount-sep{font-family:var(--sans);font-size:13px;color:var(--t4);margin-top:14px;}
/* actions */
.gp-card-actions{display:flex;gap:6px;flex-wrap:wrap;}
.gp-act-btn{height:24px;padding:0 9px;border-radius:var(--r2);border:0.5px solid var(--s3);background:transparent;font-family:var(--sans);font-size:11px;color:var(--t2);cursor:pointer;transition:all .1s;}
.gp-act-btn:hover{background:var(--s2);}
.gp-act-done{color:var(--accent-g);border-color:color-mix(in srgb,var(--accent-g) 30%,transparent);}
.gp-act-del{color:var(--t4);}
/* completed */
.gp-completed-card{background:var(--s1);border:0.5px solid var(--s2);border-left:2px solid var(--accent-g);border-radius:var(--r3);margin-bottom:8px;overflow:hidden;transition:border-color .15s;}
.gp-completed-main{padding:13px 16px 13px 14px;cursor:pointer;display:flex;align-items:center;gap:10px;}
.gp-completed-title{font-family:var(--sans);font-size:14px;color:var(--t2);}
.gp-completed-meta{font-family:var(--sans);font-size:11px;color:var(--t4);margin-top:2px;}
/* form */
.gp-form{background:var(--s1);border:0.5px solid var(--accent-s);border-radius:var(--r3);padding:16px;margin-bottom:var(--sp4);}
.gp-form-hdr{font-family:var(--sans);font-size:12px;font-weight:500;color:var(--ink);margin-bottom:14px;}
.gp-field{margin-bottom:12px;}
.gp-lbl{font-family:var(--sans);font-size:11px;color:var(--t3);margin-bottom:4px;display:block;}
.gp-input{width:100%;height:44px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r2);padding:0 10px;font-family:var(--sans);font-size:16px;color:var(--ink);outline:none;box-sizing:border-box;transition:border-color .1s;}
.gp-input:focus{border-color:var(--accent);}
.gp-textarea{width:100%;min-height:56px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r2);padding:8px 10px;font-family:var(--sans);font-size:16px;color:var(--ink);outline:none;box-sizing:border-box;resize:vertical;line-height:1.5;}
.gp-textarea:focus{border-color:var(--accent);}
.gp-pill-row{display:flex;gap:4px;flex-wrap:wrap;}
.gp-pill{height:26px;padding:0 10px;border-radius:var(--r2);border:0.5px solid var(--b2);background:var(--s2);font-family:var(--sans);font-size:var(--fs-xs);color:var(--t3);cursor:pointer;transition:all .1s;}
.gp-pill-active{background:var(--accent);border-color:var(--accent);color:var(--on-accent);font-weight:500;}
.gp-ms-row{display:flex;align-items:center;justify-content:space-between;padding:5px 0;border-bottom:0.5px solid var(--s2);}
.gp-ms-text{font-family:var(--sans);font-size:12px;color:var(--ink);}
.gp-ms-rm{background:transparent;border:none;color:var(--t4);cursor:pointer;font-size:14px;padding:0 4px;}
.gp-ms-add{display:flex;gap:6px;margin-top:6px;}
.gp-ms-input{flex:1;height:30px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r);padding:0 8px;font-family:var(--sans);font-size:12px;color:var(--ink);outline:none;}
.gp-form-actions{display:flex;gap:8px;justify-content:flex-end;margin-top:14px;}
.gp-save-btn{height:44px;padding:0 20px;background:var(--accent);border:none;border-radius:var(--r2);font-family:var(--sans);font-size:var(--fs-sm);font-weight:500;color:var(--on-accent);cursor:pointer;}
.gp-cancel-btn{height:44px;padding:0 20px;background:transparent;border:0.5px solid var(--b2);border-radius:var(--r2);font-family:var(--sans);font-size:var(--fs-sm);color:var(--t2);cursor:pointer;}

/* icon picker */
.gip-wrap{background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r3);padding:12px 14px;margin-top:6px;}
.gip-cat-lbl{font-family:var(--sans);font-size:10px;color:var(--t3);letter-spacing:.05em;text-transform:lowercase;margin-bottom:6px;margin-top:8px;}
.gip-cat-lbl:first-child{margin-top:0;}
.gip-grid{display:flex;flex-wrap:wrap;gap:4px;margin-bottom:4px;}
.gip-tile{width:36px;height:36px;border-radius:var(--r2);border:0.5px solid var(--b2);background:var(--s1);color:var(--t2);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .1s;-webkit-tap-highlight-color:transparent;}
.gip-tile:hover{border-color:var(--accent);color:var(--accent);}
.gip-tile-sel{background:var(--accent);border-color:var(--accent);color:var(--on-accent);}
/* icon display on card */
.gp-card-icon{width:28px;height:28px;border-radius:var(--r2);background:var(--s2);display:flex;align-items:center;justify-content:center;color:var(--t2);flex-shrink:0;}

/* empty */
.gp-empty{text-align:center;padding:var(--sp10) 0;color:var(--t3);font-size:13px;}
.gp-empty-icon{font-size:24px;margin-bottom:8px;opacity:.4;font-family:var(--sans);}
/* completed top */
.gp-completed-top{display:flex;align-items:center;gap:16px;padding:10px 14px;background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);margin-bottom:var(--sp4);}
.gp-completed-stat-n{font-family:var(--sans);font-size:20px;font-weight:300;color:var(--accent-g);}
.gp-completed-stat-l{font-family:var(--sans);font-size:10px;color:var(--t3);letter-spacing:.04em;}
`;

  return (
    <>
      <style>{CSS}</style>
      <div className="gp">
        {/* Header */}
        <div className="gp-hdr">
          <CyclingTitle pageLabel="goals" filters={VIEWS} value={view} onChange={setView} alwaysShowSuffix={true} />
          <button className="gp-add-btn" onClick={openAdd}>+ goal</button>
        </div>

        {/* Form */}
        {showForm && (
          <GoalForm form={form} setForm={setForm} onSave={saveGoal} onCancel={() => { setShowForm(false); setEditId(null); }} editId={editId} />
        )}

        {/* ACTIVE VIEW */}
        {view === 'active' && (
          <>
            {active.length === 0 && !showForm ? (
              <div className="gp-empty">
                <div className="gp-empty-icon">◎</div>
                <div style={{fontFamily:'var(--sans)',fontSize:14,color:'var(--t2)',marginBottom:12}}>no active goals yet</div>
                <div style={{display:'flex',flexDirection:'column',gap:6,width:'100%',maxWidth:280}}>
                  {[
                    { Icon: Activity,   label:'set a race or fitness goal'},
                    { Icon: DollarSign, label:'set a savings target'},
                    { Icon: Sparkles,   label:'add a personal goal'},
                  ].map(({ Icon, label }) => (
                    <button key={label} onClick={() => setShowForm(true)} style={{
                      background:'var(--s2)', border:'0.5px solid var(--b1)',
                      borderRadius:'var(--r2)', padding:'9px 12px',
                      fontFamily:'var(--sans)', fontSize:13, color:'var(--t2)',
                      cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:8,
                      WebkitTapHighlightColor:'transparent',
                    }}>
                      <Icon size={14} strokeWidth={1.5} color="var(--accent)" style={{ opacity:0.7, flexShrink:0 }} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Race hero card */}
                {raceGoal && <RaceHeroCard goal={raceGoal} workouts={workouts} setTab={setTab} />}

                {/* Carousel — only when there's data */}
                {active.length > 0 && <GoalCarousel goals={goals} completedCount={completed.length} />}

                {/* Non-race goals */}
                {sortedActive.length > 0 && (
                  <>
                    {raceGoal && <div className="gp-section-lbl">other goals</div>}
                    {sortedActive.map(g => (
                      <GoalCard key={g.id} g={g}
                        expanded={expandedId === g.id}
                        onToggle={() => setExpandedId(expandedId === g.id ? null : g.id)}
                        onEdit={() => { openEdit(g); setExpandedId(null); }}
                        onComplete={() => { markComplete(g.id); setExpandedId(null); }}
                        onArchive={() => archiveGoal(g.id)}
                        onDelete={() => { deleteGoal(g.id); setExpandedId(null); }}
                        onProgressChange={updateProgress}
                        isCompleted={false}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* COMPLETED VIEW */}
        {view === 'completed' && (
          <>
            {sortedCompleted.length === 0 ? (
              <div className="gp-empty">
                <div className="gp-empty-icon" style={{display:"flex",justifyContent:"center"}}><Sparkles size={22} strokeWidth={1.5} color="var(--t4)" /></div>
                <div>no completed goals yet</div>
              </div>
            ) : (
              <>
                <div className="gp-completed-top">
                  <div>
                    <div className="gp-completed-stat-n">{sortedCompleted.length}</div>
                    <div className="gp-completed-stat-l">completed</div>
                  </div>
                </div>
                {sortedCompleted.map(g => {
                  const isExp = expandedId === g.id;
                  const tookDays = g.createdAt && g.completedAt
                    ? Math.round((new Date(g.completedAt) - new Date(g.createdAt)) / 86400000)
                    : null;
                  return (
                    <div key={g.id} className="gp-completed-card">
                      <div className="gp-completed-main" onClick={() => setExpandedId(isExp ? null : g.id)}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="gp-completed-title">{g.title}</div>
                          <div className="gp-completed-meta">
                            {g.type} · {g.completedAt ? fmtDate(g.completedAt) : 'completed'}
                            {tookDays !== null && tookDays > 0 && ` · took ${tookDays}d`}
                          </div>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                          <circle cx="7" cy="7" r="6.5" stroke="var(--accent-g)" strokeWidth="1" />
                          <path d="M4 7l2 2 4-4" stroke="var(--accent-g)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      {isExp && (
                        <div className="gp-card-expand">
                          {g.note && <div className="gp-card-note">{g.note}</div>}
                          <div className="gp-card-actions">
                            <button className="gp-act-btn" onClick={() => archiveGoal(g.id)}>restore</button>
                            <button className="gp-act-btn gp-act-del" onClick={() => deleteGoal(g.id)}>delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
